'use client';

import type { SocialLink } from '@/components/social';
import type { NavMainProps } from './nav/types';
import type { FooterProps } from '../components/footer';
import type { MainSectionProps, HeaderSectionProps } from '../core';

import { useBoolean } from 'minimal-shared/hooks';

import { Logo } from '@/components/logo';
import { Iconify } from '@/components/iconify';
import { Box, IconButton } from '@/components/ui';

import { SignInButton } from '@/containers/layouts/components/sign-in-button';

import { usePathname } from '@/routes/hooks';

import { NavMobile } from './nav/mobile';
import { NavDesktop } from './nav/desktop';
import { Footer } from '../components/footer';
import { MainSection, LayoutSection, HeaderSection } from '../core';

export type MainLayoutProps = {
  navData?: NavMainProps['data'];
  socialLinksData?: SocialLink[];
  footerNavigationData?: any;
  children: React.ReactNode;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
    footer?: FooterProps;
  };
};

export function MainLayout({
  navData = [],
  socialLinksData = [],
  footerNavigationData = [],
  children,
  slotProps,
}: MainLayoutProps) {
  const pathname = usePathname();

  const isHomePage = pathname === '/';

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const renderHeader = () => {
    const headerSlots: HeaderSectionProps['slots'] = {
      leftArea: (
        <>
          <NavMobile
            data={navData}
            socialLinksData={socialLinksData}
            open={open}
            onClose={onClose}
          />
          <Logo className="w-[109px] md:w-[109px] h-[43px] lg:w-[170px] lg:h-[67px]" />
        </>
      ),
      centerArea: <NavDesktop data={navData} className="hidden lg:flex" />,
      rightArea: (
        <Box className="flex items-center gap-0">
          <SignInButton />

          <IconButton onClick={onOpen} className="lg:hidden px-0">
            <Iconify icon="lucide:menu" width={34} />
          </IconButton>
        </Box>
      ),
    };

    return (
      <HeaderSection
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={slotProps?.header?.slotProps}
        className={isHomePage ? '!fixed bg-white/80 backdrop-blur w-full' : ''}
      />
    );
  };

  const renderFooter = () => (
    <Footer
      socialLinksData={socialLinksData}
      footerNavigationData={footerNavigationData}
      {...slotProps?.footer}
    />
  );

  const renderMain = () => <MainSection>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
    >
      {renderMain()}
    </LayoutSection>
  );
}
